import { Clock, Globe, MapPin, Phone, CalendarDays, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/categories";
import { DirectionsMenu } from "@/components/DirectionsMenu";
import { track } from "@/lib/track";
import { RemindBell } from "@/components/RemindBell";

const tel = (phone) => `tel:${phone.replace(/[^\d+]/g, "")}`;

function Row({ icon: Icon, children }) {
  return (
    <div className="flex gap-3 text-sm text-slate-300">
      <Icon className="h-4 w-4 mt-0.5 text-[#00E5A3] shrink-0" />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function ScheduleList({ item, city }) {
  if (!item.schedule?.length) return null;
  return (
    <div className="space-y-2">
      <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Upcoming dates & times</h4>
      <div className="grid gap-2">
        {item.schedule.map((s, i) => (
          <div key={`${s.date}-${s.time}-${s.title}`} data-testid={`schedule-item-${i}`} className="flex justify-between items-center rounded-xl bg-[#0B0F19] border border-[#26334D] px-3 py-2 text-sm">
            <span className="truncate pr-3">{s.title || item.name}</span>
            <span className="flex items-center gap-2 shrink-0">
              <span className="font-mono-vv text-[#00E5A3]">{fmtDate(s.date)} · {s.time}</span>
              <RemindBell item={item} show={s} city={city} tid={`remind-schedule-${i}`} compact />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function InfoRows({ item }) {
  const hours = (item.hours || "").split(";").map((h) => h.trim()).filter(Boolean);
  return (
    <div className="space-y-3">
      {hours.length > 0 && <Row icon={Clock}>{hours.map((h) => <div key={h}>{h}</div>)}</Row>}
      {item.address && <Row icon={MapPin}>{item.address}</Row>}
      {item.phone && <Row icon={Phone}><a onClick={() => track(item, "call")} href={tel(item.phone)} className="hover:text-white">{item.phone}</a></Row>}
      {item.website && <Row icon={Globe}><a data-testid="listing-website-link" onClick={() => track(item, "website")} href={item.website} target="_blank" rel="noopener noreferrer" className="hover:text-white break-all">{item.website}</a></Row>}
      {item.price_range && <Row icon={Info}>Price: {item.price_range}</Row>}
    </div>
  );
}

const primary = "rounded-full h-12 bg-[#FF3B6B] hover:bg-[#E02E5A] text-white font-semibold";

function PrimaryAction({ item }) {
  if (item.phone) {
    return (
      <Button asChild className={primary}>
        <a data-testid="detail-call-button" onClick={() => track(item, "call")} href={tel(item.phone)}><Phone className="h-4 w-4 mr-1.5" /> Call now</a>
      </Button>
    );
  }
  if (!item.website) return null;
  return (
    <Button asChild className={primary}>
      <a href={item.website} target="_blank" rel="noopener noreferrer"><Globe className="h-4 w-4 mr-1.5" /> Website</a>
    </Button>
  );
}

export function DetailActions({ item }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <DirectionsMenu item={item} full />
      <PrimaryAction item={item} />
    </div>
  );
}

const SOURCE_NOTES = {
  google: ["verified-note", "Name, address, phone and hours verified by Google Places."],
  ai: ["ai-note", "Curated by our AI concierge and refreshed daily. Please confirm times with the venue before heading out."],
};

export function SourceNote({ item }) {
  const note = SOURCE_NOTES[item.source];
  return note ? <p data-testid={note[0]} className="text-[11px] text-slate-500">{note[1]}</p> : null;
}
